import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, RotateCw, Trash2, Loader2 } from "lucide-react";
import * as pdfjs from "pdfjs-dist";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
).toString();

/** Comprime [1,2,3,5] em "1-3, 5". */
function toRanges(nums) {
    const sorted = [...nums].sort((a, b) => a - b);
    const out = [];
    let start = null;
    let prev = null;
    for (const n of sorted) {
        if (start === null) { start = prev = n; continue; }
        if (n === prev + 1) { prev = n; continue; }
        out.push(start === prev ? `${start}` : `${start}-${prev}`);
        start = prev = n;
    }
    if (start !== null) out.push(start === prev ? `${start}` : `${start}-${prev}`);
    return out.join(", ");
}

/**
 * Grade visual de miniaturas do PDF (renderizado no navegador, sem upload).
 * mode: "reorder" | "remove" | "rotate"
 * onChange(options): emite {order} | {pages} | {rotations} prontos para a API.
 */
export function PageGrid({ file, mode, onChange }) {
    const [pages, setPages] = useState([]); // [{num, dataUrl}]
    const [order, setOrder] = useState([]); // nums na ordem atual
    const [removed, setRemoved] = useState(new Set());
    const [rotations, setRotations] = useState({}); // num -> 0|90|180|270
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [error, setError] = useState(null);
    const dragIdx = useRef(null);
    const renderTask = useRef(0);

    useEffect(() => {
        let cancelled = false;
        const taskId = ++renderTask.current;
        setPages([]); setOrder([]); setRemoved(new Set()); setRotations({}); setError(null);
        setProgress({ done: 0, total: 0 });

        (async () => {
            let loadingTask;
            try {
                const buf = await file.arrayBuffer();
                loadingTask = pdfjs.getDocument({ data: buf });
                const doc = await loadingTask.promise;
                if (cancelled || taskId !== renderTask.current) return;
                const total = doc.numPages;
                setProgress({ done: 0, total });
                const nums = Array.from({ length: total }, (_, i) => i + 1);
                setOrder(nums);
                const rendered = [];
                for (let i = 1; i <= total; i++) {
                    if (cancelled || taskId !== renderTask.current) { loadingTask.destroy().catch(() => {}); return; }
                    const page = await doc.getPage(i);
                    const base = page.getViewport({ scale: 1 });
                    const scale = 150 / base.width;
                    const viewport = page.getViewport({ scale });
                    const canvas = document.createElement("canvas");
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;
                    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
                    rendered.push({ num: i, dataUrl: canvas.toDataURL("image/jpeg", 0.7) });
                    setPages([...rendered]);
                    setProgress({ done: i, total });
                }
                loadingTask.destroy().catch(() => {});
            } catch (err) {
                console.error("PageGrid render error:", err);
                if (!cancelled) setError(`Não foi possível pré-visualizar este PDF (${err?.message || err}). Use o modo texto abaixo.`);
            }
        })();
        return () => { cancelled = true; };
    }, [file]);

    // emissao de opcoes para o pai
    useEffect(() => {
        if (mode === "reorder" && order.length) onChange({ order: order.join(", ") });
    }, [order, mode]); // eslint-disable-line
    useEffect(() => {
        if (mode === "remove") onChange({ pages: toRanges(removed) });
    }, [removed, mode]); // eslint-disable-line
    useEffect(() => {
        if (mode === "rotate") {
            const spec = Object.entries(rotations)
                .filter(([, deg]) => deg !== 0)
                .map(([num, deg]) => `${num}:${deg}`)
                .join(",");
            onChange({ rotations: spec });
        }
    }, [rotations, mode]); // eslint-disable-line

    if (error) {
        return <p className="text-sm text-amber-800 bg-[#FFFBEB] border-2 border-black p-3" data-testid="pagegrid-error">{error}</p>;
    }
    if (!pages.length) {
        return (
            <p className="font-mono-tag text-xs text-gray-600 flex items-center gap-2 py-6 justify-center" data-testid="pagegrid-loading">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                Renderizando páginas… {progress.total ? `${progress.done}/${progress.total}` : ""}
            </p>
        );
    }

    const move = (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= order.length) return;
        const next = [...order];
        [next[idx], next[j]] = [next[j], next[idx]];
        setOrder(next);
    };

    const toggleRemove = (num) => {
        setRemoved((prev) => {
            const next = new Set(prev);
            if (next.has(num)) next.delete(num);
            else next.add(num);
            return next;
        });
    };

    const cycleRotate = (num) => {
        setRotations((prev) => ({ ...prev, [num]: ((prev[num] || 0) + 90) % 360 }));
    };

    const byNum = Object.fromEntries(pages.map((p) => [p.num, p]));
    const shownNums = mode === "reorder" ? order : pages.map((p) => p.num);

    return (
        <div data-testid="page-grid">
            <p className="font-mono-tag text-[11px] text-gray-600 mb-2">
                {mode === "reorder" && "Arraste as miniaturas (ou use ← →) para definir a nova ordem."}
                {mode === "remove" && "Clique nas páginas para marcá-las para remoção. Clique de novo para desfazer."}
                {mode === "rotate" && "Clique no botão de girar em cada página até chegar à orientação certa."}
                {progress.done < progress.total && ` · renderizando ${progress.done}/${progress.total}…`}
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[420px] overflow-y-auto p-1">
                {shownNums.map((num, idx) => {
                    const p = byNum[num];
                    if (!p) return null;
                    const isRemoved = removed.has(num);
                    const deg = rotations[num] || 0;
                    return (
                        <div
                            key={num}
                            draggable={mode === "reorder"}
                            onDragStart={() => (dragIdx.current = idx)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                                if (dragIdx.current === null) return;
                                const next = [...order];
                                const [moved] = next.splice(dragIdx.current, 1);
                                next.splice(idx, 0, moved);
                                setOrder(next);
                                dragIdx.current = null;
                            }}
                            onClick={() => mode === "remove" && toggleRemove(num)}
                            role={mode === "remove" ? "button" : undefined}
                            tabIndex={mode === "remove" ? 0 : undefined}
                            onKeyDown={(e) => mode === "remove" && (e.key === "Enter" || e.key === " ") && toggleRemove(num)}
                            aria-pressed={mode === "remove" ? isRemoved : undefined}
                            aria-label={`Página ${num}${isRemoved ? " (marcada para remover)" : ""}`}
                            data-testid={`page-thumb-${num}`}
                            className={`relative border-2 bg-white select-none transition-colors ${
                                mode === "reorder" ? "cursor-grab" : ""
                            } ${mode === "remove" ? "cursor-pointer" : ""} ${
                                isRemoved ? "border-red-700 opacity-40" : "border-black"
                            }`}
                        >
                            <img
                                src={p.dataUrl}
                                alt={`Página ${num} do documento`}
                                className="w-full block transition-transform"
                                style={{ transform: deg ? `rotate(${deg}deg) scale(${deg % 180 ? 0.75 : 1})` : undefined }}
                                draggable={false}
                            />
                            <span className="absolute top-0 left-0 px-1.5 py-0.5 bg-black text-[#22D3EE] font-mono-tag text-[10px] font-bold">
                                {mode === "reorder" ? `${idx + 1}º · pág ${num}` : `pág ${num}`}
                            </span>
                            {mode === "remove" && isRemoved && (
                                <span className="absolute inset-0 flex items-center justify-center">
                                    <Trash2 className="w-6 h-6 text-red-700" aria-hidden="true" />
                                </span>
                            )}
                            {mode === "rotate" && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); cycleRotate(num); }}
                                    aria-label={`Girar página ${num}`}
                                    data-testid={`rotate-page-${num}`}
                                    className="absolute bottom-0 right-0 p-1.5 bg-black text-[#22D3EE] hover:bg-[#0284C7] hover:text-white transition-colors"
                                >
                                    <RotateCw className="w-3.5 h-3.5" />
                                </button>
                            )}
                            {mode === "reorder" && (
                                <span className="absolute bottom-0 right-0 flex">
                                    <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} aria-label={`Mover página ${num} para a esquerda`} data-testid={`move-left-${num}`} className="p-1 bg-black text-[#22D3EE] disabled:opacity-30">
                                        <ArrowLeft className="w-3 h-3" />
                                    </button>
                                    <button type="button" onClick={() => move(idx, 1)} disabled={idx === order.length - 1} aria-label={`Mover página ${num} para a direita`} data-testid={`move-right-${num}`} className="p-1 bg-black text-[#22D3EE] disabled:opacity-30 border-l border-[#0284C7]">
                                        <ArrowRight className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
